import { Formik, Form, Field } from 'formik';
import { format, parseISO } from 'date-fns';
import { CheckCircle, XCircle } from 'lucide-react';

import { notifySuccess } from '@/portainer/services/notifications';
import { pluralize } from '@/react/common/string-utils';

import { FormControl } from '@@/form-components/FormControl';
import { LoadingButton } from '@@/buttons/LoadingButton';
import { Input } from '@@/form-components/Input';
import { SwitchField } from '@@/form-components/SwitchField';
import { Tooltip } from '@@/Tip/Tooltip';

import { useBackupLocalSettings } from './queries/useBackupLocalSettings';
import { useBackupLocalStatus } from './queries/useBackupLocalStatus';
import { useUpdateBackupLocalSettingsMutation } from './queries/useUpdateBackupLocalSettingsMutation';
import { useRunLocalBackupNowMutation } from './queries/useRunLocalBackupNowMutation';
import { BackupLocalModel, BackupLocalSettings } from './types';
import { validationSchema } from './BackupLocalForm.validation';

// Go's time.Time zero value serialises with year 0001
const GO_ZERO_TIME_PREFIX = '0001';
const BACKUP_TIMESTAMP_FORMAT = 'dd MMM yyyy, HH:mm';

export function BackupLocalForm({ onSave }: { onSave?: () => void }) {
  const updateLocalMutate = useUpdateBackupLocalSettingsMutation();
  const runNowMutate = useRunLocalBackupNowMutation();

  const settingsQuery = useBackupLocalSettings();
  const statusQuery = useBackupLocalStatus({
    enabled: !!settingsQuery.data?.cronRule,
  });

  if (settingsQuery.isLoading) {
    return null;
  }

  const settings = settingsQuery.data;
  const status = statusQuery.data;

  const initialValues: BackupLocalSettings = {
    scheduleAutomaticBackup: !!settings?.cronRule,
    cronRule: settings?.cronRule || '',
    retentionDays: settings?.retentionDays ?? 7,
  };

  return (
    <>
      <Formik<BackupLocalSettings>
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        validateOnMount
        enableReinitialize
      >
        {({ values, errors, isSubmitting, setFieldValue, isValid }) => (
          <Form className="form-horizontal">
            <div className="form-group">
              <div className="col-sm-12">
                <SwitchField
                  name="schedule-automatic-backup"
                  data-cy="settings-scheduleAutomaticLocalBackupSwitch"
                  labelClass="col-sm-3 col-lg-2"
                  label="Schedule automatic backups"
                  checked={values.scheduleAutomaticBackup}
                  onChange={(e) => setFieldValue('scheduleAutomaticBackup', e)}
                />
              </div>
            </div>

            {values.scheduleAutomaticBackup && (
              <>
                <FormControl
                  inputId="local_cron_rule"
                  label="Cron rule"
                  size="small"
                  errors={errors.cronRule}
                  required
                >
                  <Field
                    id="local_cron_rule"
                    name="cronRule"
                    type="text"
                    as={Input}
                    placeholder="0 2 * * *"
                    data-cy="settings-localBackupCronRuleInput"
                  />
                </FormControl>

                <FormControl
                  inputId="local_retention_days"
                  label="Retention (days)"
                  size="small"
                  tooltip="Number of days to keep backups. Set to 0 to keep backups indefinitely."
                  errors={errors.retentionDays}
                >
                  <Field
                    id="local_retention_days"
                    name="retentionDays"
                    type="number"
                    min="0"
                    as={Input}
                    data-cy="settings-localBackupRetentionDaysInput"
                  />
                </FormControl>
              </>
            )}

            <div className="form-group">
              <div className="col-sm-12">
                <LoadingButton
                  loadingText="Saving settings..."
                  isLoading={isSubmitting}
                  disabled={!isValid || isSubmitting}
                  className="!ml-0"
                  data-cy="settings-saveLocalBackupSettingsButton"
                >
                  Save backup settings
                </LoadingButton>
              </div>
            </div>
          </Form>
        )}
      </Formik>

      {settings?.cronRule && (
        <div className="form-horizontal mt-4">
          <hr />
          <div className="form-group">
            <div className="col-sm-3 col-lg-2 control-label text-left">
              Active schedule
            </div>
            <div className="col-sm-9 col-lg-10">
              <p className="text-muted mt-1">
                <code>{settings.cronRule}</code>
                {settings.retentionDays > 0
                  ? ` — keeping backups for ${
                      settings.retentionDays
                    } ${pluralize(settings.retentionDays, 'day')}`
                  : ' — keeping backups indefinitely'}
              </p>
            </div>
          </div>

          {status?.path && (
            <div className="form-group">
              <div className="col-sm-3 col-lg-2 control-label flex items-center gap-1 text-left">
                Backup location
                <Tooltip message="This is the path inside the Portainer container. It corresponds to the host directory or network share you mounted at this location via your Docker volume configuration." />
              </div>
              <div className="col-sm-9 col-lg-10">
                <p className="text-muted mt-1">
                  <code>{status.path}</code>
                </p>
              </div>
            </div>
          )}

          <div className="form-group">
            <div className="col-sm-3 col-lg-2 control-label text-left">
              Last backup
            </div>
            <div className="col-sm-9 col-lg-10">
              {status?.timestampUTC &&
              !status.timestampUTC.startsWith(GO_ZERO_TIME_PREFIX) ? (
                <p className="mt-1 flex items-center gap-2">
                  {status.failed ? (
                    <XCircle className="text-error" size={16} />
                  ) : (
                    <CheckCircle className="text-success" size={16} />
                  )}
                  <span>
                    {format(
                      parseISO(status.timestampUTC),
                      BACKUP_TIMESTAMP_FORMAT
                    )}{' '}
                    UTC
                    {status.failed && (
                      <span className="text-error ml-1">(failed)</span>
                    )}
                  </span>
                </p>
              ) : (
                <p className="text-muted mt-1">No backups have run yet</p>
              )}
            </div>
          </div>

          <div className="form-group">
            <div className="col-sm-12">
              <LoadingButton
                loadingText="Running backup..."
                isLoading={runNowMutate.isLoading}
                className="!ml-0"
                color="default"
                data-cy="settings-runLocalBackupNowButton"
                onClick={() =>
                  runNowMutate.mutate(undefined, {
                    onSuccess() {
                      notifySuccess('Success', 'Backup completed successfully');
                    },
                  })
                }
                type="button"
              >
                Run backup now
              </LoadingButton>
            </div>
          </div>
        </div>
      )}
    </>
  );

  async function onSubmit(values: BackupLocalSettings) {
    const payload: BackupLocalModel = {
      cronRule: values.scheduleAutomaticBackup ? values.cronRule : '',
      retentionDays: values.scheduleAutomaticBackup ? values.retentionDays : 0,
    };

    updateLocalMutate.mutate(payload, {
      onSuccess() {
        notifySuccess('Success', 'Local backup settings saved successfully');
        onSave?.();
      },
    });
  }
}
